import { Content, Header, Item, Trigger } from '@radix-ui/react-accordion';
import { Icon, IconsEnum, toIconsEnum } from 'components/Icons';
import React, { useMemo } from 'react';
import { closeMenu, openMenu } from '../../liveNavSlice';

import { ItemWithTranslation } from 'types/swagger';
import { LiveNavActionPayload } from '../../types';
import classnames from 'classnames';
import styles from './LiveNavAccordion.module.scss';
import stylesContent from 'features/sport/components/sportNav/sportNavAccordion/components/sportNavAccordionContent/SportNavAccordionContent.module.scss';
import stylesItem from 'features/sport/components/sportNav/sportNavAccordion/SportNavAccordion.module.scss';
import stylesTrigger from 'features/sport/components/sportNav/sportNavAccordion/components/sportNavAccordionTrigger/SportNavAccordionTrigger.module.scss';
import { useAppDispatch } from 'lib/centralStore';
import { useIsExpanded } from '../../hooks';
import { useTranslation } from 'hooks/useLingUI';
import { Counter } from '../counter/Counter';

export type LiveNavAccordionProps = ItemWithTranslation<{ description: string }> & {
  icon?: IconsEnum;
  entryId: string;
  counter?: number | null;
  children?: React.ReactNode;
  className?: string;
  idDisciplina?: number | string;
  iconFavorite?: IconsEnum;
};

export const LiveNavAccordion = ({
  icon,
  entryId,
  className,
  idDisciplina,
  iconFavorite,
  counter,
  children,
  description,
  descriptionTrKey,
}: LiveNavAccordionProps) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const isExpanded = useIsExpanded(entryId);

  const onAccordionToggleHandler = async (): Promise<void> => {
    const nextSt = isExpanded ? false : true;
    const payload = { entryId: entryId } as LiveNavActionPayload;
    const toggleMenuAction = nextSt ? openMenu : closeMenu;
    await dispatch(toggleMenuAction(payload));
  };

  const objFav = useMemo(() => {
    if (iconFavorite) {
      return <Icon iconId={iconFavorite} className={styles.iconFavorite} color="var(--color-on-bg-primary)" />;
    }

    return null;
  }, [iconFavorite]);

  const objDisciplina = useMemo(() => {
    const iconId = toIconsEnum(`DISCIPLINA_${idDisciplina}`);
    if (iconId) {
      return <Icon iconId={iconId} className={styles.iconDisciplina} color="var(--color-on-bg-primary)" />;
    }
    return null;
  }, [idDisciplina]);

  const objIcon = useMemo(() => {
    const iconId = icon ?? (isExpanded ? IconsEnum.MINUS : IconsEnum.PLUS);
    return (
      <Icon iconId={iconId} className={`${stylesTrigger.icon} ${styles.icon}`} color="var(--color-on-bg-secondary)" />
    );
  }, [icon, isExpanded]);

  const txt = t(descriptionTrKey, description);

  return (
    <Item
      key={entryId}
      value={entryId}
      className={classnames(stylesItem.item, styles.item)}
      data-testid={entryId}
      suppressHydrationWarning
    >
      <Header title={txt}>
        <Trigger
          onClick={onAccordionToggleHandler}
          className={classnames(stylesTrigger.container, className)}
          suppressHydrationWarning
        >
          {objDisciplina}
          {objFav}
          <div className={styles.triggerWrapper} suppressHydrationWarning>
            <span className={stylesTrigger.text} suppressHydrationWarning={true}>
              {txt}
            </span>
            {counter && (
              <Counter isLive className={styles.counter} counter={counter} idDisciplina={`${idDisciplina}`} />
            )}
          </div>
          {objIcon}
        </Trigger>
      </Header>
      <Content className={stylesContent.container} key={`${entryId}-${isExpanded}`} suppressHydrationWarning>
        {children}
      </Content>
    </Item>
  );
};
